import { assertBlobIntegrity, createBlobRef } from './bytes.js';
import { StorageError, isQuotaError, mapStorageError } from './errors.js';
import { assertBlobRef } from './validation.js';
const BLOB_DIRECTORY = 'tchoma-blobs-v1';
function errorName(error) {
    if (typeof DOMException !== 'undefined' && error instanceof DOMException)
        return error.name;
    if (error && typeof error === 'object' && 'name' in error && typeof error.name === 'string')
        return error.name;
    return undefined;
}
function isMissingEntry(error) {
    return errorName(error) === 'NotFoundError';
}
export class OpfsBlobStore {
    fallback;
    storageManager;
    clock;
    directoryPromise = null;
    opfsUsable;
    constructor(fallback, storageManager, clock) {
        this.fallback = fallback;
        this.storageManager = storageManager;
        this.clock = clock;
        this.opfsUsable = typeof storageManager?.getDirectory === 'function';
    }
    get backendKind() {
        return this.opfsUsable ? 'opfs' : 'indexeddb';
    }
    async directory() {
        if (!this.opfsUsable || !this.storageManager?.getDirectory)
            return null;
        if (!this.directoryPromise) {
            this.directoryPromise = this.storageManager.getDirectory()
                .then((root) => root.getDirectoryHandle(BLOB_DIRECTORY, { create: true }))
                .catch((error) => {
                if (isQuotaError(error))
                    throw mapStorageError(error);
                this.opfsUsable = false;
                return null;
            });
        }
        return this.directoryPromise;
    }
    async putPrepared(ref, input) {
        assertBlobRef(ref);
        await assertBlobIntegrity(input, ref);
        const directory = await this.directory();
        if (!directory) {
            await this.fallback.putPrepared(ref, input);
            return;
        }
        let writable = null;
        try {
            const file = await directory.getFileHandle(ref.id, { create: true });
            writable = await file.createWritable();
            await writable.write(input);
            await writable.close();
            writable = null;
            await this.fallback.putReference(ref);
        }
        catch (error) {
            try {
                await writable?.abort?.();
            }
            catch {
                // Keep the original storage failure.
            }
            if (isQuotaError(error))
                throw mapStorageError(error);
            this.opfsUsable = false;
            this.directoryPromise = null;
            await this.fallback.putPrepared(ref, input);
        }
    }
    async put(input, options = {}) {
        const ref = await createBlobRef(input, options.mime ?? (input.type || 'application/octet-stream'), this.clock);
        await this.putPrepared(ref, input);
        return ref;
    }
    async get(ref) {
        assertBlobRef(ref);
        const directory = await this.directory();
        if (directory) {
            try {
                const handle = await directory.getFileHandle(ref.id);
                const file = await handle.getFile();
                await assertBlobIntegrity(file, ref);
                return file.slice(0, file.size, ref.mime);
            }
            catch (error) {
                if (error instanceof StorageError)
                    throw error;
                if (!isMissingEntry(error)) {
                    this.opfsUsable = false;
                    this.directoryPromise = null;
                }
            }
        }
        return this.fallback.get(ref);
    }
    async delete(ref) {
        assertBlobRef(ref);
        let opfsError;
        const directory = await this.directory();
        if (directory) {
            try {
                await directory.removeEntry(ref.id);
            }
            catch (error) {
                if (!isMissingEntry(error))
                    opfsError = error;
            }
        }
        await this.fallback.delete(ref);
        if (opfsError)
            throw mapStorageError(opfsError);
    }
    list(options) {
        return this.fallback.list(options);
    }
}
//# sourceMappingURL=opfs.js.map